import { DraggableLocation } from 'react-beautiful-dnd';
import { DraggableBlob, Module } from '@myme/utils';
import { v4 as uuidv4 } from 'uuid';
import { parseStringToInt } from 'src/utils';

/// //////////////////////////////////////
export const addNewModule = (index: number): Module => ({
  id: uuidv4(),
  name: `Module ${index}`,
  blobs: [],
  isNew: true
});

/// //////////////////////////////////////
export const editModuleName = (moduleId: string, moduleName: string) => {
  const newModulesState = (prevState: Module[]) =>
    prevState.map(module => {
      const moduleIdDoesNotMatch = module.id !== moduleId;
      if (moduleIdDoesNotMatch) {
        return module;
      }

      return {
        ...module,
        name: moduleName
      };
    });

  return newModulesState;
};

/// //////////////////////////////////////
export const removeBlob = (moduleSrcIndex: number, blobIndex: number) => {
  const newModulesState = (prevState: Module[]) =>
    prevState.map((module, index) => {
      const indexDoesNotMatch = index !== moduleSrcIndex;
      if (indexDoesNotMatch) {
        return module;
      }

      return {
        ...module,
        blobs: module.blobs.filter((_b, index) => index !== blobIndex)
      };
    });

  return newModulesState;
};

/// //////////////////////////////////////
export const reorderBlobs = <T>(list: T[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

/// //////////////////////////////////////
export const moveBlobFromOneModuleToAnother = (
  source: Module,
  destination: Module,
  droppableSource: DraggableLocation,
  droppableDestination: DraggableLocation
) => {
  const sourceBlobsClone = Array.from(source.blobs);
  const destBlobsClone = Array.from(destination.blobs);
  const [removed] = sourceBlobsClone.splice(droppableSource.index, 1);
  destBlobsClone.splice(droppableDestination.index, 0, removed);

  return [sourceBlobsClone, destBlobsClone];
};

/// //////////////////////////////////////
export const moveBlobToAnotherModule = (
  srcBlobs: DraggableBlob[],
  destBlobs: DraggableBlob[],
  moduleSrcIndex: number,
  moduleDestinationIndex: number
) => {
  const newModulesState = (prevState: Module[]) =>
    prevState.map((module, index) => {
      const indexDoesNotMatch = index !== moduleSrcIndex && index !== moduleDestinationIndex;
      if (indexDoesNotMatch) {
        return module;
      }

      return {
        ...module,
        blobs: index === moduleSrcIndex ? srcBlobs : destBlobs
      };
    });

  return newModulesState;
};

export const moveBlobInTheSameModule = (
  blobs: DraggableBlob[],
  source: DraggableLocation,
  destination: DraggableLocation
) => {
  const moduleIndex = parseStringToInt(source.droppableId);
  const reorderedModuleBlobs = reorderBlobs(blobs, source.index, destination.index);

  const newModulesState = (prevState: Module[]) =>
    prevState.map((module, index) => (moduleIndex !== index ? module : { ...module, blobs: reorderedModuleBlobs }));

  return newModulesState;
};

/// //////////////////////////////////////
export const addBlobFromLibraryToModule = (
  blob: DraggableBlob,
  destination: Module,
  droppableDestination: DraggableLocation,
  activeAfterSeconds: number | null
) => {
  const moduleDestinationIndex = parseStringToInt(droppableDestination.droppableId);
  const copyOfBlob: DraggableBlob = { ...blob, dragId: uuidv4(), activeAfterSeconds };

  const destBlobsClone = Array.from(destination.blobs);
  destBlobsClone.splice(droppableDestination.index, 0, copyOfBlob);

  const newModulesState = (prevState: Module[]) =>
    prevState.map((module, index) => {
      const indexDoesNotMatch = index !== moduleDestinationIndex;
      if (indexDoesNotMatch) {
        return module;
      }

      return {
        ...module,
        blobs: destBlobsClone
      };
    });

  return newModulesState;
};
